import { classifyEyeState } from './metrics';

const WASM_BASE = '/ai/mediapipe/wasm';
const MODEL_PATH = '/ai/mediapipe/models/face_landmarker.task';
let landmarkerPromise;
const identityLandmarkerPromises = new Map();

function getCategoryScore(categories, name) {
  return categories.find(category => category.categoryName === name)?.score || 0;
}

async function createLandmarker(numFaces = 12, outputFaceBlendshapes = true) {
  const { FaceLandmarker } = await import('@mediapipe/tasks-vision');
  const wasmFileset = {
    wasmLoaderPath: `${WASM_BASE}/vision_wasm_internal.js`,
    wasmBinaryPath: `${WASM_BASE}/vision_wasm_internal.wasm`,
  };
  return FaceLandmarker.createFromOptions(wasmFileset, {
    baseOptions: { modelAssetPath: MODEL_PATH },
    runningMode: 'IMAGE',
    numFaces,
    minFaceDetectionConfidence: 0.45,
    minFacePresenceConfidence: 0.45,
    outputFaceBlendshapes,
  });
}

function averagePoint(landmarks, indices) {
  const points = indices.map(index => landmarks[index]);
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

function getRecognitionKeypoints(landmarks) {
  const eyes = [
    averagePoint(landmarks, [33, 133]),
    averagePoint(landmarks, [362, 263]),
  ].sort((left, right) => left.x - right.x);
  const mouth = [landmarks[61], landmarks[291]].sort((left, right) => left.x - right.x);
  return [eyes[0], eyes[1], landmarks[1], mouth[0], mouth[1]].map(point => ({ x: point.x, y: point.y }));
}

function getFaceBounds(landmarks) {
  const xs = landmarks.map(point => point.x);
  const ys = landmarks.map(point => point.y);
  return {
    left: Math.max(0, Math.min(...xs)),
    top: Math.max(0, Math.min(...ys)),
    right: Math.min(1, Math.max(...xs)),
    bottom: Math.min(1, Math.max(...ys)),
  };
}

function getFaceLandmarker() {
  if (!landmarkerPromise) {
    landmarkerPromise = createLandmarker().catch(error => {
      landmarkerPromise = undefined;
      throw error;
    });
  }
  return landmarkerPromise;
}

async function detectFaceGeometry(imageSource, numFaces = 50) {
  let promise = identityLandmarkerPromises.get(numFaces);
  if (!promise) {
    promise = createLandmarker(numFaces, false).catch(error => {
      identityLandmarkerPromises.delete(numFaces);
      throw error;
    });
    identityLandmarkerPromises.set(numFaces, promise);
  }
  const landmarker = await promise;
  const result = landmarker.detect(imageSource);
  return result.faceLandmarks.map((landmarks, index) => ({
    index,
    bounds: getFaceBounds(landmarks),
    keypoints: getRecognitionKeypoints(landmarks),
  }));
}

async function analyzeFaces(imageSource) {
  const landmarker = await getFaceLandmarker();
  const result = landmarker.detect(imageSource);
  const faces = result.faceLandmarks.map((landmarks, index) => {
    const categories = result.faceBlendshapes[index]?.categories || [];
    const leftBlink = getCategoryScore(categories, 'eyeBlinkLeft');
    const rightBlink = getCategoryScore(categories, 'eyeBlinkRight');
    const bounds = getFaceBounds(landmarks);
    // Head rotation often makes one blendshape score lower even when both eyes
    // are visibly closed. Require a strong average plus evidence on both eyes.
    const { eyesClosed, possibleWink } = classifyEyeState(leftBlink, rightBlink);
    return {
      leftBlink: Number(leftBlink.toFixed(3)),
      rightBlink: Number(rightBlink.toFixed(3)),
      eyesClosed,
      possibleWink,
      bounds,
    };
  });
  return {
    status: 'ready',
    faceCount: faces.length,
    closedEyeFaces: faces.filter(face => face.eyesClosed).length,
    possibleWinks: faces.filter(face => face.possibleWink).length,
    faces,
  };
}

export { analyzeFaces, detectFaceGeometry, getFaceLandmarker, getRecognitionKeypoints };
